/**
 * Centralized site-wide marketing stats for Fridge.com.
 *
 * Every runtime page that displays product counts, article counts, tool counts,
 * etc. should import from here instead of hardcoding. Update this ONE file
 * when numbers change — every page reflects the new values automatically.
 *
 * These are the owner-approved coverage display set, not live database counts.
 * Keep measured populations separately qualified; do not rederive this display set.
 */

export const SITE_STATS = {
  /**
   * Raised 5,000+ → 6,000+ on 08/31/2026 — owner dictated the display set
   * verbatim on FRIDGE-690 ('6,000+ products, 500+ brands', with the plus),
   * superseding FRIDGE-487's 5,000+. Live active products measured 5,848
   * that day: the figure reads over the tracked corpus per the standing
   * internal-real/external-marketing split.
   */
  products: '6,000+',
  articles: '15,000+',
  tools: '90',
  cities: '32,000+',
  /**
   * Raised 500+ → 1,000+ on 08/17/2026 (owner: "we do way more retailers and
   * brands than you currently show"), floored on the 1,158 brand-table rows.
   * RETURNED to 500+ on 08/22/2026 by the newer owner ruling (FRIDGE-487:
   * "5,000+ products and 500+ brands — some accidentally say 1k brands").
   * The two rulings point opposite ways; the newer, explicit one governs.
   * Live counts 08/23/2026:
   *   select count(distinct brand) from products                  -> 1,848
   *   select count(distinct brand) from products where active     ->   410
   * 500+ is TRUE of tracked breadth (1,848) and ABOVE the live-product span
   * (410). The sentences that render this figure say "track … from N brands",
   * which reads over the tracked corpus — so the floor sits below its own
   * definition's truth. If the owner ever wants the live-product definition,
   * the honest floor today is 400+.
   */
  brands: '500+',
  /** Short form for badges, metadata, “Trusted by …” */
  users: '1.5M+',
  /** Long numeric form for definitions / schema */
  usersLong: 'over 1,500,000',
  usersNumeric: 'over 1,500,000',
  /** Opening clause for “have used Fridge.com …” copy */
  usersMilestone: 'Over 1.5 million consumers have used Fridge.com',
  /** Footer/trust clause including longevity */
  usersMilestoneSince: 'Since 2021, over 1.5 million consumers have used Fridge.com',
  collections: '50,000+',
  states: '50',
  statesFull: 'All 50 states + DC',
} as const

/** Historical in-stock retailer snapshot, measured August 17, 2026.
 * Keep only for explicitly dated historical reporting. This is not a current
 * live count and must not feed generic marketing or "today" claims.
 * The footer obtains current in-stock coverage from its live RPC instead.
 */
export const RETAILERS_TRACKED = 15
export const RETAILERS_TRACKED_AS_OF = 'August 17, 2026'

/** Retailer integration snapshot underlying the approved display set.
 * The owner-approved public coverage label is RETAILER_INTEGRATIONS_DISPLAY;
 * neither value substitutes for a product's actual offer count.
 */
export const RETAILER_INTEGRATIONS = 25

/**
 * Marketing display form of RETAILER_INTEGRATIONS — owner ruling 08/21/2026:
 * copy says "25+ retailers and manufacturer stores", sitewide. The "+" is
 * floored on the exact 25 integration rows (37 advertiser rows sit above it),
 * so the figure never sits above the truth. Keep the numeric constant for
 * arithmetic; use this one in rendered copy.
 */
export const RETAILER_INTEGRATIONS_DISPLAY = '25+'
